// src/pages/gerenciar/TabelaAtendimentosPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { nivelUsuario } from "../../utils/permissoes";
import { NIVEIS } from "../../config/niveis";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import FiltroBar from "../../components/FiltroBar/FiltroBar";

import { db } from "../../config/firebase";
import { collection, getDocs, getDoc, query, where, doc, deleteDoc, updateDoc, serverTimestamp, Timestamp } from "firebase/firestore";

import { formatData, formatDataHora } from "../../utils/dateFormat";
import { getCorStatus, isStatusBloqueado } from "../../config/status";
import { useSortableTable, SortableHeader } from "../../hooks/useSortableTable";

const ATENDIMENTOS_CACHE_TTL_MS = 60000;
const STATUS_ATEND_CACHE_TTL_MS = 300000;
const atendimentosCache = new Map();
const statusMapCache = new Map();

function renderCodigoOs(atendimento) {
  if (atendimento.osCodigo) return atendimento.osCodigo;
  if (atendimento.osCodigoSequencial != null) {
    return `OS${String(atendimento.osCodigoSequencial).padStart(4, "0")}`;
  }
  if (atendimento.osId) return String(atendimento.osId).slice(0, 8);
  return "-";
}


function formatParaInput(data) {
  if (!data) return "";
  const d = data?.toDate ? data.toDate() : (data instanceof Date ? data : new Date(data));
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}


function parseInputDateTimeLocal(value) {
  if (!value) return null;
  // value: YYYY-MM-DDTHH:mm
  try {
    const [datePart, timePart] = String(value).split("T");
    if (!datePart || !timePart) return null;
    const [y, m, d] = datePart.split("-").map((n) => parseInt(n, 10));
    const [hh, mm] = timePart.split(":").map((n) => parseInt(n, 10));
    if (![y, m, d, hh, mm].every((n) => Number.isFinite(n))) return null;
    return new Date(y, m - 1, d, hh, mm, 0, 0);
  } catch {
    return null;
  }
}

function ChevronIcon({ expanded }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        transition: "transform 0.2s ease",
        transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
      }}
    >
      <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
  );
}

function TabelaAtendimentosPage() {
  const { tema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const grupoId = usuarioLogado?.grupoId || null;
  const loadRequestRef = useRef(0);
  const nivel = nivelUsuario(usuarioLogado);
  const podeEditarHorarios = nivel >= NIVEIS.GESTOR;
  const navigate = useNavigate();

  const [atendimentos, setAtendimentos] = useState([]);
  const [statusMap, setStatusMap] = useState({});
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [deletando, setDeletando] = useState(null);


  // ---- Modal: editar horários ----
  const [modalHorariosOpen, setModalHorariosOpen] = useState(false);
  const [atendimentoEdit, setAtendimentoEdit] = useState(null);
  const [editInicio, setEditInicio] = useState("");
  const [editFim, setEditFim] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editErro, setEditErro] = useState("");
  const [editSucesso, setEditSucesso] = useState("");

  const [textoFiltro, setTextoFiltro] = useState("");
  const [fEmpresa, setFEmpresa] = useState("todas");
  const [fSetor, setFSetor] = useState("todas");
  const [fStatus, setFStatus] = useState("todas");
  const [fParada, setFParada] = useState("todas");

  useEffect(() => {
    async function carregarDados() {
      if (!grupoId) return;

      const requestId = loadRequestRef.current + 1;
      loadRequestRef.current = requestId;
      const isStale = () => loadRequestRef.current !== requestId;

      const cachedStatus = statusMapCache.get(grupoId);
      const cachedAtend = atendimentosCache.get(grupoId);

      const hasStatusCache = cachedStatus && Date.now() - cachedStatus.at < STATUS_ATEND_CACHE_TTL_MS;
      const hasAtendCache = cachedAtend && Date.now() - cachedAtend.at < ATENDIMENTOS_CACHE_TTL_MS;

      if (hasStatusCache && hasAtendCache) {
        if (!isStale()) {
          setErro("");
          setStatusMap(cachedStatus.data);
          setAtendimentos(cachedAtend.data);
          setCarregando(false);
        }
        return;
      }

      setCarregando(true);
      setErro("");

      try {
        const statusRef = collection(db, "statusOs");
        const ref = collection(db, "atendimentos");
        const statusQuery = query(statusRef, where("grupoId", "==", grupoId));
        const q = query(ref, where("grupoId", "==", grupoId));

        const [statusSnap, snap] = await Promise.all([
          hasStatusCache ? Promise.resolve(null) : getDocs(statusQuery),
          hasAtendCache ? Promise.resolve(null) : getDocs(q),
        ]);

        if (isStale()) return;

        let mapaStatus = hasStatusCache ? cachedStatus.data : {};
        if (!hasStatusCache && statusSnap) {
          mapaStatus = {};
          statusSnap.docs.forEach((docSnap) => {
            const data = docSnap.data();
            const nome = (data.nome || "").toLowerCase().trim();
            if (nome) {
              mapaStatus[nome] = data.cor || "#64748b";
            }
          });
          statusMapCache.set(grupoId, { at: Date.now(), data: mapaStatus });
        }

        let lista = hasAtendCache ? cachedAtend.data : [];
        if (!hasAtendCache && snap) {
          lista = snap.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }));
          atendimentosCache.set(grupoId, { at: Date.now(), data: lista });
        }

        if (!isStale()) {
          setStatusMap(mapaStatus);
          setAtendimentos(lista);
        }
      } catch (e) {
        console.error("Erro ao carregar atendimentos:", e);
        if (!isStale()) {
          setErro("Erro ao carregar Tabela de Atendimentos. Tente novamente.");
        }
      } finally {
        if (!isStale()) {
          setCarregando(false);
        }
      }
    }

    carregarDados();

    return () => {
      loadRequestRef.current += 1;
    };
  }, [grupoId]);


  function abrirEditarHorarios(at) {
    if (!podeEditarHorarios) return;
    setAtendimentoEdit(at);
    setEditInicio(formatParaInput(at?.dataInicio));
    setEditFim(formatParaInput(at?.dataFim));
    setEditErro("");
    setEditSucesso("");
    setModalHorariosOpen(true);
  }

  function fecharEditarHorarios() {
    if (editSaving) return;
    setModalHorariosOpen(false);
    setAtendimentoEdit(null);
    setEditErro("");
    setEditSucesso("");
  }

  async function salvarEditarHorarios() {
    if (!podeEditarHorarios) return;
    if (!atendimentoEdit?.id) return;

    setEditErro("");
    setEditSucesso("");

    const dtInicio = parseInputDateTimeLocal(editInicio);
    const dtFim = editFim ? parseInputDateTimeLocal(editFim) : null;

    if (!dtInicio || Number.isNaN(dtInicio.getTime())) {
      setEditErro("Informe um 'Início do atendimento' válido.");
      return;
    }

    if (dtFim && Number.isNaN(dtFim.getTime())) {
      setEditErro("Informe um 'Fim do atendimento' válido (ou deixe em branco).");
      return;
    }

    if (dtFim && dtFim.getTime() < dtInicio.getTime()) {
      setEditErro("O 'Fim do atendimento' não pode ser anterior ao 'Início'.");
      return;
    }

    setEditSaving(true);

    try {
      const inicioTs = Timestamp.fromDate(dtInicio);
      const fimTs = dtFim ? Timestamp.fromDate(dtFim) : null;

      // 1) Atualiza o atendimento
      const atendRef = doc(db, "atendimentos", atendimentoEdit.id);
      await updateDoc(atendRef, {
        dataInicio: inicioTs,
        dataFim: fimTs,
        atualizadoEm: serverTimestamp(),
        editadoHorariosEm: serverTimestamp(),
        editadoHorariosPor: usuarioLogado?.uid || null,
        editadoHorariosPorNome: usuarioLogado?.nome || null,
      });

      // 2) Atualiza a OS (se existir)
      if (atendimentoEdit?.osId) {
        const osRef = doc(db, "ordensServico", atendimentoEdit.osId);
        const osSnap = await getDoc(osRef);
        if (osSnap.exists()) {
          const os = osSnap.data() || {};

          const osUpdate = {
            ultimoAtendimentoInicioEm: inicioTs,
            ultimoAtendimentoFimEm: fimTs,
            editadoHorariosEm: serverTimestamp(),
            editadoHorariosPor: usuarioLogado?.uid || null,
            editadoHorariosPorNome: usuarioLogado?.nome || null,
          };

          const continua = String(atendimentoEdit?.maquinaContinuaParada ?? "")
            .toLowerCase()
            .trim();
          const disseNao = ["nao", "não", "n", "false", "0", "no"].includes(continua);

          const parouSim = String(os?.maquinaParou || "")
            .toLowerCase()
            .trim() === "sim";

          // Se a OS é de máquina parada e este atendimento liberou a máquina,
          // alinhar o fim da parada com o 'Fim do atendimento' editado.
          if (parouSim && disseNao && fimTs) {
            osUpdate.maquinaVoltouRodarEm = fimTs;
            osUpdate.maquinaParadaEncerradaEm = fimTs;
            osUpdate.maquinaParadaAtiva = false;
            osUpdate.maquinaContinuaParadaUltimo = "Não";
          }

          // Se a OS já está bloqueada (Finalizada/Cancelada/etc),
          // ajustar timestamps de fim para refletir o horário editado (KPIs/Dashboard).
          if (fimTs && isStatusBloqueado(os?.statusNome)) {
            osUpdate.atualizadoEm = fimTs;
            osUpdate.updatedAt = fimTs;
            osUpdate.updatedEm = fimTs;
            osUpdate.finalizadoEm = fimTs;
          }

          await updateDoc(osRef, osUpdate);
        }
      }

      // Atualiza a lista em tela
      setAtendimentos((prev) =>
        prev.map((a) =>
          a.id === atendimentoEdit.id
            ? { ...a, dataInicio: inicioTs, dataFim: fimTs }
            : a
        )
      );

      setEditSucesso("Horários atualizados com sucesso.");
      // fecha depois de um instante para o usuário ver a mensagem
      setTimeout(() => {
        setModalHorariosOpen(false);
      }, 450);
    } catch (e) {
      console.error("Erro ao editar horários:", e);
      const code = e?.code || "";
      if (code === "permission-denied") {
        setEditErro("Sem permissão para editar horários.");
      } else {
        setEditErro("Erro ao salvar horários. Tente novamente.");
      }
    } finally {
      setEditSaving(false);
    }
  }


  const empresasUnicas = useMemo(() => {
    const map = new Map();
    for (const at of atendimentos) {
      if (at.empresaId) {
        map.set(at.empresaId, at.empresaNome || at.empresaId);
      }
    }
    return Array.from(map.entries()).map(([id, nome]) => ({ id, nome }));
  }, [atendimentos]);

  const setoresUnicos = useMemo(() => {
    const map = new Map();
    for (const at of atendimentos) {
      if (at.setorId) {
        map.set(at.setorId, at.setorNome || at.setorId);
      }
    }
    return Array.from(map.entries()).map(([id, nome]) => ({ id, nome }));
  }, [atendimentos]);

  const statusUnicos = useMemo(() => {
    const set = new Set();
    for (const at of atendimentos) {
      if (at.statusAtendimento) {
        set.add(at.statusAtendimento);
      }
    }
    return Array.from(set);
  }, [atendimentos]);

  const atendimentosFiltrados = useMemo(() => {
    let lista = [...atendimentos];

    const filtroTexto = textoFiltro.trim().toLowerCase();

    if (filtroTexto) {
      lista = lista.filter((at) => {
        const campos = [
          renderCodigoOs(at),
          at.empresaNome,
          at.setorNome,
          at.maquinaNome,
at.defeitoApresentado,
          at.defeitoEncontrado,
          at.servicoExecutado,
          at.tecnicoNome,
          at.statusAtendimento,
        ];
        return campos
          .filter(Boolean)
          .some((valor) => String(valor).toLowerCase().includes(filtroTexto));
      });
    }

    if (fEmpresa !== "todas") {
      lista = lista.filter((at) => at.empresaId === fEmpresa);
    }

    if (fSetor !== "todas") {
      lista = lista.filter((at) => at.setorId === fSetor);
    }

    if (fStatus !== "todas") {
      lista = lista.filter((at) => at.statusAtendimento === fStatus);
    }

    if (fParada === "sim") {
      lista = lista.filter(
        (at) =>
          String(at.maquinaContinuaParada || "")
            .toLowerCase()
            .startsWith("s")
      );
    } else if (fParada === "nao") {
      lista = lista.filter(
        (at) =>
          String(at.maquinaContinuaParada || "")
            .toLowerCase()
            .startsWith("n")
      );
    }

    lista.sort((a, b) => {
      const tA = a.osDataAbertura?.toMillis?.() ?? 0;
      const tB = b.osDataAbertura?.toMillis?.() ?? 0;
      if (tA !== tB) return tA - tB;

      const sA = a.subnumero ?? 0;
      const sB = b.subnumero ?? 0;
      return sA - sB;
    });

    return lista;
  }, [atendimentos, textoFiltro, fEmpresa, fSetor, fStatus, fParada]);

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const { sortedData, requestSort, getSortDirection } = useSortableTable(
    atendimentosFiltrados,
    { key: "osDataAbertura", direction: "desc" }
  );

  const handleVerDetalhes = (at) => {
    if (at.osId) {
      navigate(`/ordens-servico/status`);
    }
  };

  const handleEditar = (at) => {
    if (at.osId) {
      navigate(`/ordens-servico/atendimento/${at.osId}`);
    }
  };

  const handleDeletar = async (at) => {
    if (!at.id || !at.osId) return;

    const atendimentosOS = atendimentos.filter((a) => a.osId === at.osId && a.subnumero != null);
    
    if (atendimentosOS.length > 1) {
      const maxSub = Math.max(...atendimentosOS.map((a) => a.subnumero ?? 0));
      const atSub = at.subnumero ?? 0;

      if (atSub < maxSub) {
        alert(
          `Para deletar este atendimento (Sub ${at.subnumero}), primeiro delete o Sub ${maxSub}. Atendimentos devem ser deletados em ordem decrescente.`
        );
        return;
      }
    }

    const confirmar = window.confirm(
      `Tem certeza que deseja deletar o atendimento Sub ${at.subnumero} da OS ${renderCodigoOs(at)}?`
    );
    if (!confirmar) return;

    setDeletando(at.id);
    try {
      await deleteDoc(doc(db, "atendimentos", at.id));
      setAtendimentos((prev) => prev.filter((a) => a.id !== at.id));
    } catch (e) {
      console.error("Erro ao deletar atendimento:", e);
      setErro("Erro ao deletar atendimento. Tente novamente.");
    } finally {
      setDeletando(null);
    }
  };

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Tabela de Atendimentos"
      subtitulo="Gerenciar / Tabela de Atendimentos"
    >
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Tabela de Atendimentos</h3>
            <p>
              Visão geral dos atendimentos realizados nas Ordens de Serviço,
              com filtros por empresa, setor, status e se a máquina continua
              parada.
            </p>
          </div>
        </header>

        <FiltroBar
          campo="texto"
          campos={[]}
          textoFiltro={textoFiltro}
          onTextoFiltroChange={setTextoFiltro}
          onCampoChange={null}
          onAdicionarClick={null}
          placeholder="Filtrar por código, empresa, máquina, defeitos, serviço, técnico..."
          mostrarAdicionar={false}
        />

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 12,
            alignItems: "center",
          }}
        >
          <select
            value={fEmpresa}
            onChange={(e) => setFEmpresa(e.target.value)}
            style={{
              padding: "6px 10px",
              borderRadius: 999,
              border: "1px solid #334155",
              
              fontSize: 12,
            }}
          >
            <option value="todas">Todas as empresas</option>
            {empresasUnicas.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.nome}
              </option>
            ))}
          </select>

          <select
            value={fSetor}
            onChange={(e) => setFSetor(e.target.value)}
            style={{
              padding: "6px 10px",
              borderRadius: 999,
              border: "1px solid #334155",
              
              fontSize: 12,
            }}
          >
            <option value="todas">Todos os setores</option>
            {setoresUnicos.map((setor) => (
              <option key={setor.id} value={setor.id}>
                {setor.nome}
              </option>
            ))}
          </select>

          <select
            value={fStatus}
            onChange={(e) => setFStatus(e.target.value)}
            style={{
              padding: "6px 10px",
              borderRadius: 999,
              border: "1px solid #334155",
              
              fontSize: 12,
            }}
          >
            <option value="todas">Todos status</option>
            {statusUnicos.map((st, idx) => (
              <option key={idx} value={st}>
                {st}
              </option>
            ))}
          </select>

          <select
            value={fParada}
            onChange={(e) => setFParada(e.target.value)}
            style={{
              padding: "6px 10px",
              borderRadius: 999,
              border: "1px solid #334155",
              
              fontSize: 12,
            }}
          >
            <option value="todas">Máquina parada? (todas)</option>
            <option value="sim">Somente continua parada</option>
            <option value="nao">Somente liberada</option>
          </select>
        </div>

        <div className="pc-table-wrapper">
          {erro && (
            <div
              style={{
                background: "#7f1d1d",
                color: "#fecaca",
                padding: 8,
                borderRadius: 8,
                marginBottom: 12,
              }}
            >
              {erro}
            </div>
          )}

          {carregando ? (
            <p style={{ padding: 10 }}>Carregando atendimentos...</p>
          ) : sortedData.length === 0 ? (
            <p style={{ padding: 10 }}>
              Nenhum atendimento encontrado para os filtros selecionados.
            </p>
          ) : (
            <table className="pc-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}></th>
                  <SortableHeader
                    label="ID OS"
                    sortKey="osCodigoSequencial"
                    requestSort={requestSort}
                    getSortDirection={getSortDirection}
                  />
                  <SortableHeader
                    label="SUB"
                    sortKey="subnumero"
                    requestSort={requestSort}
                    getSortDirection={getSortDirection}
                  />
                  <SortableHeader
                    label="Abertura"
                    sortKey="osDataAbertura"
                    requestSort={requestSort}
                    getSortDirection={getSortDirection}
                  />
                  <SortableHeader
                    label="Empresa"
                    sortKey="empresaNome"
                    requestSort={requestSort}
                    getSortDirection={getSortDirection}
                  />
                  <SortableHeader
                    label="Máquina"
                    sortKey="maquinaNome"
                    requestSort={requestSort}
                    getSortDirection={getSortDirection}
                  />
                  <th>Defeito</th>
                  <SortableHeader
                    label="Técnico"
                    sortKey="tecnicoNome"
                    requestSort={requestSort}
                    getSortDirection={getSortDirection}
                  />
                  <SortableHeader
                    label="Status"
                    sortKey="statusAtendimento"
                    requestSort={requestSort}
                    getSortDirection={getSortDirection}
                  />
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((at) => {
                  const isExpanded = expandedId === at.id;
                  const fotos = Array.isArray(at.fotos) ? at.fotos : [];
                  
                  return (
                    <React.Fragment key={at.id}>
                      <tr
                        style={{ cursor: "pointer" }}
                        onClick={() => toggleExpand(at.id)}
                      >
                        <td style={{ textAlign: "center", padding: "8px 4px" }}>
                          <button
                            className="pc-btn-icon"
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? "Recolher detalhes" : "Expandir detalhes"}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpand(at.id);
                            }}
                            style={{
                              background: isExpanded
                                ? "rgba(249, 115, 22, 0.2)"
                                : "transparent",
                              border: "1px solid rgba(148, 163, 184, 0.3)",
                            }}
                          >
                            <ChevronIcon expanded={isExpanded} />
                          </button>
                        </td>
                        <td style={{ fontWeight: 600 }}>{renderCodigoOs(at)}</td>
                        <td>{at.subnumero ?? "-"}</td>
                        <td style={{ whiteSpace: "nowrap", fontSize: "0.85em" }}>
                          {formatData(at.osDataAbertura)}
                        </td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{at.empresaNome || "-"}</div>
                          {at.setorNome && (
                            <div style={{ fontSize: "0.8em", opacity: 0.7 }}>{at.setorNome}</div>
                          )}
                        </td>
                        <td>
                          <div>{at.maquinaNome || "-"}</div>
</td>
                        <td style={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {at.defeitoApresentado || "-"}
                        </td>
                        <td>{at.tecnicoNome || "-"}</td>
                        <td>
                          {(() => {
                            const corStatus = getCorStatus(at.statusAtendimento, statusMap);
                            return (
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                  padding: "4px 10px",
                                  borderRadius: 999,
                                  fontSize: "0.75em",
                                  fontWeight: 600,
                                  background: `${corStatus}20`,
                                  color: corStatus,
                                  border: `1px solid ${corStatus}40`,
                                }}
                              >
                                <span
                                  style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    background: corStatus,
                                  }}
                                />
                                {at.statusAtendimento || "-"}
                              </span>
                            );
                          })()}
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 4 }}>
                            <button
                              className="pc-btn-icon"
                              title="Ver OS"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleVerDetalhes(at);
                              }}
                              style={{ padding: 6 }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                <circle cx="12" cy="12" r="3"/>
                              </svg>
                            </button>
                            <button
                              className="pc-btn-icon"
                              title="Editar"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditar(at);
                              }}
                              style={{ padding: 6 }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                            <button
                              className="pc-btn-icon"
                              title="Deletar"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletar(at);
                              }}
                              disabled={deletando === at.id}
                              style={{ padding: 6, color: deletando === at.id ? "#94a3b8" : "#ef4444" }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                      
                      {isExpanded && (
                        <tr className="row-details-expanded">
                          <td colSpan={10} style={{ padding: 0 }}>
                            <div
                              style={{
                                background: "rgba(148, 163, 184, 0.05)",
                                borderTop: "1px solid rgba(148, 163, 184, 0.15)",
                                borderBottom: "1px solid rgba(148, 163, 184, 0.15)",
                                padding: "16px 20px",
                                animation: "fadeIn 0.2s ease-out",
                              }}
                            >
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                                  gap: 16,
                                }}
                              >
                                <div>
                                  <div style={{ fontSize: "0.75em", opacity: 0.6, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                    Início do Atendimento
                                  </div>
                                  <div style={{ fontWeight: 500 }}>{formatDataHora(at.dataInicio)}</div>
                                </div>

                                <div>
                                  <div style={{ fontSize: "0.75em", opacity: 0.6, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                    Fim do Atendimento
                                  </div>
                                  <div style={{ fontWeight: 500 }}>{formatDataHora(at.dataFim)}</div>

                                  {podeEditarHorarios && (
                                    <div style={{ marginTop: 12 }}>
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); abrirEditarHorarios(at); }}
                                        style={{
                                          padding: "8px 12px",
                                          borderRadius: 10,
                                          border: "1px solid rgba(148, 163, 184, 0.35)",
                                          background: "rgba(148, 163, 184, 0.12)",
                                          color: "inherit",
                                          cursor: "pointer",
                                          fontWeight: 700,
                                        }}
                                      >
                                        Editar horários
                                      </button>
                                    </div>
                                  )}

                                </div>

                                <div>
                                  <div style={{ fontSize: "0.75em", opacity: 0.6, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                    Setor
                                  </div>
                                  <div style={{ fontWeight: 500 }}>{at.setorNome || "-"}</div>
                                </div>
                                <div style={{ gridColumn: "span 2" }}>
                                  <div style={{ fontSize: "0.75em", opacity: 0.6, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                    Defeito Encontrado
                                  </div>
                                  <div>{at.defeitoEncontrado || "-"}</div>
                                </div>

                                <div style={{ gridColumn: "span 2" }}>
                                  <div style={{ fontSize: "0.75em", opacity: 0.6, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                    Serviço Executado
                                  </div>
                                  <div>{at.servicoExecutado || "-"}</div>
                                </div>

                                <div>
                                  <div style={{ fontSize: "0.75em", opacity: 0.6, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                    Máquina Continua Parada?
                                  </div>
                                  <div style={{ fontWeight: 500 }}>
                                    {at.maquinaContinuaParada === "Sim" ? (
                                      <span style={{ color: "#ef4444" }}>Sim - Parada</span>
                                    ) : (
                                      <span style={{ color: "#22c55e" }}>Não - Liberada</span>
                                    )}
                                  </div>
                                </div>

                                <div>
                                  <div style={{ fontSize: "0.75em", opacity: 0.6, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                    Fotos
                                  </div>
                                  <div style={{ fontWeight: 500 }}>
                                    {fotos.length > 0 ? `${fotos.length} foto(s)` : "Nenhuma foto"}
                                  </div>
                                </div>

                                {at.observacoes && (
                                  <div style={{ gridColumn: "1 / -1" }}>
                                    <div style={{ fontSize: "0.75em", opacity: 0.6, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                      Observações
                                    </div>
                                    <div style={{ whiteSpace: "pre-wrap" }}>{at.observacoes}</div>
                                  </div>
                                )}
                              </div>

                              <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                                <button
                                  className="pc-btn-secundario"
                                  style={{ padding: "8px 16px", fontSize: "0.85em" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleVerDetalhes(at);
                                  }}
                                >
                                  Ver OS
                                </button>
                                <button
                                  className="pc-btn-secundario"
                                  style={{ padding: "8px 16px", fontSize: "0.85em" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditar(at);
                                  }}
                                >
                                  Editar Atendimento
                                </button>
                                <button
                                  className="pc-btn-secundario"
                                  style={{ 
                                    padding: "8px 16px", 
                                    fontSize: "0.85em",
                                    color: "#ef4444",
                                    borderColor: "#ef4444"
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeletar(at);
                                  }}
                                  disabled={deletando === at.id}
                                >
                                  {deletando === at.id ? "Deletando..." : "Deletar"}
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {modalHorariosOpen && (
        <div
          onClick={fecharEditarHorarios}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 9999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="pc-card"
            style={{
              width: "min(720px, 100%)",
              padding: 16,
              borderRadius: 16,
              border: "1px solid rgba(148, 163, 184, 0.18)",
              boxSizing: "border-box",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 900 }}>Editar horários do atendimento</div>
                <div style={{ marginTop: 2, fontSize: 13, opacity: 0.85 }}>
                  {atendimentoEdit ? (
                    <>
                      <strong>{renderCodigoOs(atendimentoEdit)}</strong>
                      {atendimentoEdit?.maquinaNome ? ` • ${atendimentoEdit.maquinaNome}` : ""}
                      {atendimentoEdit?.tecnicoNome ? ` • ${atendimentoEdit.tecnicoNome}` : ""}
                    </>
                  ) : null}
                </div>
                <div style={{ marginTop: 6, fontSize: 12, opacity: 0.75 }}>
                  Esse ajuste atualiza os horários no <strong>Atendimento</strong> e, quando aplicável, os campos de parada/fechamento na <strong>OS</strong>.
                </div>
              </div>

              <button
                type="button"
                className="pc-btn-icon"
                onClick={fecharEditarHorarios}
                title="Fechar"
                style={{ padding: 8 }}
                disabled={editSaving}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18" />
                  <path d="M6 6l12 12" />
                </svg>
              </button>
            </div>

            {editErro && (
              <div
                style={{
                  marginTop: 12,
                  padding: 10,
                  borderRadius: 10,
                  background: "rgba(127, 29, 29, 0.9)",
                  color: "#fecaca",
                  border: "1px solid rgba(239, 68, 68, 0.45)",
                  whiteSpace: "pre-wrap",
                }}
              >
                {editErro}
              </div>
            )}

            {editSucesso && (
              <div
                style={{
                  marginTop: 12,
                  padding: 10,
                  borderRadius: 10,
                  background: "rgba(6, 78, 59, 0.9)",
                  color: "#d1fae5",
                  border: "1px solid rgba(34, 197, 94, 0.45)",
                  whiteSpace: "pre-wrap",
                }}
              >
                {editSucesso}
              </div>
            )}

            <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ fontSize: 13, opacity: 0.85 }}>Início do atendimento</span>
                <input
                  type="datetime-local"
                  value={editInicio}
                  onChange={(e) => setEditInicio(e.target.value)}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid rgba(148, 163, 184, 0.35)",
                    background: "rgba(15, 23, 42, 0.35)",
                    color: "inherit",
                  }}
                />
              </label>

              <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ fontSize: 13, opacity: 0.85 }}>Fim do atendimento</span>
                <input
                  type="datetime-local"
                  value={editFim}
                  onChange={(e) => setEditFim(e.target.value)}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid rgba(148, 163, 184, 0.35)",
                    background: "rgba(15, 23, 42, 0.35)",
                    color: "inherit",
                  }}
                />
              </label>
            </div>

            <div style={{ marginTop: 16, display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={fecharEditarHorarios}
                disabled={editSaving}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="pc-btn-primario"
                onClick={salvarEditarHorarios}
                disabled={editSaving}
              >
                {editSaving ? "Salvando..." : "Salvar horários"}
              </button>
            </div>
          </div>
        </div>
      )}

    </LayoutDesktop>
  );
}

export default TabelaAtendimentosPage;
